/**
 * Ni² @mention menu — a workbook-backed people picker.
 * Case-insensitive substring search against fullName, role and
 * subspecialty. Every result carries the Person ID so callers can
 * store the ID rather than a free-text name.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { getPeopleForMentionSearch } from "@/data/repository";

export default function Ni2MentionMenu({ query, onPick, onClose }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const results = useMemo(() => getPeopleForMentionSearch(query, 8), [query]);
  const rootRef = useRef(null);

  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(results.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter" && results[activeIdx]) {
        e.preventDefault();
        onPick?.(results[activeIdx]);
      } else if (e.key === "Escape") {
        onClose?.();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [results, activeIdx, onPick, onClose]);

  if (!results.length) return null;

  return (
    <div
      ref={rootRef}
      className="nn-mention-menu nn-soft-in absolute bottom-full left-8 z-40 mb-2 w-[320px]"
      role="listbox"
      data-testid="ni2-mention-menu"
    >
      <div className="px-3 pt-2 text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
        Mention a colleague
      </div>
      <div className="mt-1">
        {results.map((p, i) => {
          const initials = String(p.fullName || "")
            .split(" ")
            .map((s) => s[0])
            .filter(Boolean)
            .slice(0, 2)
            .join("")
            .toUpperCase();
          const isAttending = /attending/i.test(p.role);
          const isResident = /resident/i.test(p.role);
          const detail = isResident
            ? `PGY-${p.pgyLevel || "—"}`
            : isAttending
              ? p.subspecialty || p.role
              : p.subspecialty || p.role || "";
          return (
            <div
              key={p.personId}
              className="nn-mention-item"
              data-active={i === activeIdx || undefined}
              data-testid={`ni2-mention-item-${p.personId}`}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                onPick?.(p);
              }}
              role="option"
              aria-selected={i === activeIdx}
            >
              <span
                className="grid h-8 w-8 place-items-center rounded-full border border-white/15 bg-white/[0.06] text-[11px] font-semibold text-[hsl(var(--nn-ink))]"
                aria-hidden="true"
              >
                {initials || "·"}
              </span>
              <div className="min-w-0">
                <div className="truncate text-[12.5px] font-medium text-[hsl(var(--nn-ink))]">
                  {p.fullName}
                </div>
                <div className="truncate text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
                  {p.role}
                  {detail ? ` · ${detail}` : ""}
                </div>
              </div>
              <span className="font-mono text-[10px] text-[hsl(var(--nn-ink-faint))]">
                {p.personId}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
